/**
 * questionnaire-pdf.ts — client-side PDF renderer for the M6 export.
 *
 * Takes the JSON export payload (fetched from GET /:id/export?format=json)
 * and produces a board-ready PDF matching the R2 report design:
 *   - Cover page: wordmark, title, freshness stamp, hero evidence coverage
 *     number with segmented bar, stat strip, priority actions
 *   - Per-question A/B/C: Question → Response + Source Documentation → Frameworks
 *
 * Renders in the browser; jsPDF is a `packages/web` dep, not core.
 */

import jsPDF from 'jspdf';
import { tokens } from '@aigovops/design-system';
import type { JsonExportPayload } from '@aigovops/core';

const C = {
  text: tokens.colors.light.text,
  textMuted: tokens.colors.light.textMuted,
  textDim: tokens.colors.light.textDim,
  textFaint: tokens.colors.light.textFaint,
  accent: tokens.colors.light.accent,
  accentCta: tokens.colors.light.accentCtaBg,
  border: tokens.colors.light.border,
  borderStrong: tokens.colors.light.borderStrong,
  success: tokens.colors.light.success,
  successBg: tokens.colors.light.successBg,
  successFg: tokens.colors.light.successFg,
  warning: tokens.colors.light.warning,
  warningBg: tokens.colors.light.warningBg,
  warningFg: tokens.colors.light.warningFg,
  dangerFg: tokens.colors.light.dangerFg,
  danger: tokens.colors.light.critical,
  dangerBg: tokens.colors.light.dangerBg,
  surface: '#F5F5F2',
  quoteFill: '#FAFAF8',
  gapFill: '#FFF8F0',
};

export function renderQuestionnairePdf(doc: jsPDF, payload: JsonExportPayload): void {
  const M = 48;
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const CW = PW - M * 2;
  let y = M;

  // ---- helpers --------------------------------------------------------

  function drawFooter() {
    doc.setDrawColor(C.border);
    doc.line(M, PH - 40, PW - M, PH - 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(C.textFaint);
    doc.text(
      `${payload.questionnaire.title} · ${payload.freshness_stamp} · gacpro.io`,
      M,
      PH - 28,
    );
  }

  function nextPage() {
    doc.addPage();
    y = M;
    drawFooter();
  }

  function newPageIfNeeded(nextHeight = 20) {
    if (y + nextHeight > PH - M - 50) {
      nextPage();
      return true;
    }
    return false;
  }

  function line(text: string, size = 10, opts: { bold?: boolean; italic?: boolean; color?: string; indent?: number } = {}) {
    const indent = opts.indent ?? 0;
    newPageIfNeeded(size * 1.5);
    doc.setFontSize(size);
    doc.setFont(
      'helvetica',
      opts.bold && opts.italic ? 'bolditalic' : opts.bold ? 'bold' : opts.italic ? 'italic' : 'normal',
    );
    doc.setTextColor(opts.color ?? C.text);
    const maxWidth = CW - indent;
    const wrapped = doc.splitTextToSize(text, maxWidth);
    doc.text(wrapped, M + indent, y);
    y += wrapped.length * (size * 1.35);
  }

  function spacer(px = 8) { y += px; }

  function divider(thick = false) {
    newPageIfNeeded(12);
    doc.setDrawColor(C.border);
    doc.setLineWidth(thick ? 2 : 1);
    doc.line(M, y, PW - M, y);
    doc.setLineWidth(1);
    y += thick ? 16 : 12;
  }

  // ---- Cover ----------------------------------------------------------

  drawFooter();

  // 1. Header: wordmark + report type
  doc.setFont('times', 'italic');
  doc.setFontSize(24);
  doc.setTextColor(C.text);
  doc.text('gacpro', M, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(C.textMuted);
  doc.text('QUESTIONNAIRE RESPONSE', PW - M, y, { align: 'right' });
  y += 12;
  divider(true);

  // 2. Title + metadata
  line(payload.questionnaire.title, 16, { bold: true });
  spacer(4);
  line(
    `Generated: ${payload.generated_at.slice(0, 10)}  ·  Status: ${payload.questionnaire.status}`,
    9,
    { color: C.textDim },
  );
  spacer(4);
  line(payload.freshness_stamp, 9, { color: C.accentCta, bold: true });
  spacer(16);

  // 3. Evidence coverage hero
  const es = payload.evidence_summary;
  if (es) {
    // Label
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(C.textFaint);
    doc.text('EVIDENCE COVERAGE', M, y);
    // 48pt font has ~35pt ascender — need 46pt gap to avoid overlap
    y += 46;

    // Hero number
    const coveragePct = es.coverage_pct;
    const heroColor = coveragePct >= 80 ? C.success : coveragePct >= 50 ? C.warningFg : C.dangerFg;
    doc.setFont('times', 'bold');
    doc.setFontSize(48);
    doc.setTextColor(heroColor);
    const pctStr = String(coveragePct);
    doc.text(pctStr, M, y);
    const numW = doc.getTextWidth(pctStr);
    doc.setFontSize(24);
    doc.text('%', M + numW + 2, y);
    const fullNumW = numW + 2 + doc.getTextWidth('%');

    // Status chip — right of number
    const chipLabel = coveragePct >= 80 ? 'On track' : coveragePct >= 50 ? 'Needs attention' : 'At risk';
    const chipBg = coveragePct >= 80 ? C.successBg : coveragePct >= 50 ? C.warningBg : C.dangerBg;
    const chipFg = heroColor;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const chipTW = doc.getTextWidth(chipLabel);
    const chipW = chipTW + 16;
    const chipX = M + fullNumW + 16;
    doc.setFillColor(chipBg);
    doc.roundedRect(chipX, y - 16, chipW, 18, 3, 3, 'F');
    doc.setTextColor(chipFg);
    doc.text(chipLabel, chipX + 8, y - 5);
    y += 8;

    // Target line
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(C.textDim);
    doc.text(`of ${es.total_answers} responses`, M, y);
    y += 18;

    // Segmented evidence bar — full width
    const barH = 10;
    const segments = [
      { count: es.attested_count, color: C.success },
      { count: es.declared_count, color: C.warning },
      { count: es.ai_inferred_count, color: C.textFaint },
      { count: es.no_match_count, color: C.danger },
    ];
    let bx = M;
    for (const seg of segments) {
      if (seg.count <= 0) continue;
      const sw = Math.max((CW * seg.count) / es.total_answers, 1);
      doc.setFillColor(seg.color);
      doc.rect(bx, y, sw, barH, 'F');
      bx += sw;
    }
    y += barH + 10;

    // Legend
    const legendItems = [
      { color: C.success, label: `${es.attested_count} backed` },
      { color: C.warning, label: `${es.declared_count} partial` },
      { color: C.textFaint, label: `${es.ai_inferred_count} aspirational` },
      { color: C.danger, label: `${es.no_match_count} no match` },
    ];
    let lx = M;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    for (const item of legendItems) {
      if (item.label.startsWith('0 ')) continue;
      doc.setFillColor(item.color);
      doc.roundedRect(lx, y - 5, 8, 8, 2, 2, 'F');
      doc.setTextColor(C.textFaint);
      doc.text(item.label, lx + 12, y + 2);
      lx += doc.getTextWidth(item.label) + 28;
    }
    y += 14;
    spacer(6);
    divider();
  }

  // 4. Stat strip
  const approved = payload.answers.filter((a) => a.approved_at).length;
  const total = payload.answers.length;
  const stripH = 36;
  doc.setFillColor(C.surface);
  doc.roundedRect(M, y, CW, stripH, 4, 4, 'F');

  const stats = [
    { num: String(approved), label: 'approved', color: C.success },
    { num: String(total - approved), label: 'pending review', color: C.warningFg },
    { num: String(es?.no_match_count ?? 0), label: 'no match', color: C.danger },
  ];
  const slotW = CW / stats.length;
  let sx = M + 14;
  for (let i = 0; i < stats.length; i++) {
    const s = stats[i];
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(s.color);
    doc.text(s.num, sx, y + 23);
    const nw = doc.getTextWidth(s.num);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(C.textDim);
    doc.text(s.label, sx + nw + 5, y + 23);
    if (i < stats.length - 1) {
      const dx = M + slotW * (i + 1);
      doc.setDrawColor(C.border);
      doc.line(dx, y + 8, dx, y + stripH - 8);
    }
    sx += slotW;
  }
  y += stripH + 16;
  divider();

  // ---- Per-question answers -------------------------------------------
  for (const a of payload.answers) {
    // A/B/C layout needs ~140pt minimum; page-break before starting.
    newPageIfNeeded(140);

    // Left-edge color indicator (4pt bar)
    const originColor = a.draft_origin === 'attested' ? C.success
      : a.draft_origin === 'declared' ? C.warning
      : C.borderStrong;
    doc.setFillColor(originColor);
    doc.rect(M, y - 2, 4, 14, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(C.text);
    const seq = `Q${String(a.seq_no).padStart(2, '0')}${a.section ? ` · ${a.section}` : ''}`;
    doc.text(seq, M + 10, y);
    if (a.approved_at) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(C.accentCta);
      doc.text(
        `Approved by ${a.approved_by ?? 'reviewer'} · ${a.approved_at.slice(0, 10)}`,
        PW - M,
        y,
        { align: 'right' },
      );
    }
    y += 16;

    // A) Question
    line('A) Question', 9, { color: C.textDim, bold: true });
    line(a.question_text, 10, { color: C.text, indent: 20 });
    spacer(6);

    // B) Response + Source Documentation
    const originLabel = a.draft_origin === 'attested' ? 'evidence-backed'
      : a.draft_origin === 'declared' ? 'partial evidence'
      : 'aspirational';
    const responseLabel = a.response_source === 'preparer' ? 'Preparer'
      : a.response_source === 'llm_draft' ? 'Draft'
      : 'Blank';
    line(`B) Response (${responseLabel}, ${originLabel})`, 9, { color: C.textDim, bold: true });
    if (a.response) {
      line(a.response, 10, { color: C.text, indent: 20 });
    } else {
      line('(no response)', 9, { color: C.textMuted, italic: true, indent: 20 });
    }
    spacer(4);

    // Source documentation with excerpts
    if (a.evidence_artifacts && a.evidence_artifacts.length > 0) {
      line('Source documentation:', 8, { color: C.textDim, bold: true, indent: 20 });
      for (const ev of a.evidence_artifacts) {
        const header = [ev.artifact_title];
        if (ev.version_label) header.push(ev.version_label);
        if (ev.section_ref) header.push(`— ${ev.section_ref}`);
        line(`• ${header.join(' ')}`, 9, { color: C.textMuted, indent: 28 });

        // Quoted excerpt block
        if (ev.relevant_excerpt) {
          newPageIfNeeded(40);
          const qbX = M + 36;
          const qbW = CW - 36;
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(9);
          const excerptWrapped = doc.splitTextToSize(`"${ev.relevant_excerpt}"`, qbW - 20);
          const qbH = excerptWrapped.length * 12 + 12;

          doc.setFillColor(C.quoteFill);
          doc.rect(qbX, y - 2, qbW, qbH, 'F');
          doc.setFillColor(C.accent);
          doc.rect(qbX, y - 2, 2, qbH, 'F');
          doc.setTextColor(C.text);
          doc.text(excerptWrapped, qbX + 10, y + 8);
          y += qbH + 4;
        }

        const meta: string[] = [];
        if (ev.last_attested_at) meta.push(`Attested: ${ev.last_attested_at.slice(0, 10)}`);
        if (ev.freshness === 'overdue') meta.push('Review overdue');
        if (ev.document_url) meta.push(`Source: ${ev.document_url}`);
        if (meta.length > 0) {
          line(meta.join('  ·  '), 8, { color: C.textDim, indent: 36 });
        }
        spacer(4);
      }
    } else if (a.draft_origin === 'ai_inferred' || !a.draft_origin) {
      // Gap callout box
      newPageIfNeeded(40);
      const gX = M + 20;
      const gW = CW - 20;
      const gH = 30;
      doc.setFillColor(C.gapFill);
      doc.rect(gX, y - 2, gW, gH, 'F');
      doc.setFillColor(C.warning);
      doc.rect(gX, y - 2, 2, gH, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(C.warningFg);
      doc.text('No supporting documentation', gX + 10, y + 10);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(C.textMuted);
      doc.text('Upload policies or SOPs on the Evidence page to strengthen this response.', gX + 10, y + 22);
      y += gH + 4;
    }

    // C) Frameworks
    const frameworks = [...new Set(a.citations.flatMap((c) => c.frameworks))];
    if (frameworks.length > 0) {
      spacer(2);
      line('C) Frameworks', 9, { color: C.textDim, bold: true });
      line(frameworks.join(' · '), 9, { color: C.textMuted, indent: 20 });
    }

    // Register items
    if (a.citations.length > 0) {
      spacer(2);
      line('Register items:', 8, { color: C.textDim, indent: 20 });
      for (const c of a.citations) {
        line(`• [${c.risk_item_id}] ${c.title}`, 9, { color: C.textMuted, indent: 28 });
      }
    }
    spacer(12);
    divider();
  }

  // ---- Sign-off -------------------------------------------------------
  newPageIfNeeded(80);
  line('Sign-off', 12, { bold: true });
  spacer(4);
  const signerLine = payload.signer_user_id
    ? `Signed by: ${payload.signer_user_id}`
    : 'Signer: (unassigned)';
  line(signerLine, 10);
  line(`Freshness: ${payload.freshness_stamp}`, 9, { color: C.textMuted });
  spacer(12);
  line(
    'This response was drafted using the gacpro compliance register. Every cited item traces back to active legislation, framework releases, or case law reviewed by a named subject-matter expert.',
    9,
    { color: C.textMuted, italic: true },
  );
}
